import {
  Button, FormControl, ControlLabel, FormGroup
} from 'react-bootstrap';
import {
  useState, useEffect, ChangeEvent, MouseEventHandler
} from 'react';

import Modal from 'src/react_components/modal';
import { pluralize, snakecaseKeys } from 'src/utilities/string-helpers';
import AjaxUtilities from 'src/utilities/ajax-utilities';
import SegmentedRadioButtons from 'src/react_components/form-helpers/segmented-radio-buttons';
import LoadingButton from '../form-helpers/loading-button';
import { FooterProps, Callback } from './types.d';
import SvgIcon from '../svg-icon';

const Footer = ({
  returnToQueue = true,
  completeQA = true,
  burleyQAReviewPath,
  qaActionPath,
  qaQueuePath,
  meetingUuid,
  isQaLead,
  messageCount,
  qaResponses,
  burleyQAEnabled,
  readOnly,
  qaReturnable = true
}: FooterProps) => {
  const [sending, setSending] = useState(false);
  const [fetchCompleted, setFetchCompleted] = useState(false);
  const [openEscalateModal, setOpenEscalateModal] = useState(false);
  const [openSummaryModal, setOpenSummaryModal] = useState(false);

  const [escalateParams, setEscalateParams] = useState({
    action_reason: 'emotionally_at_risk',
    action_details: ''
  });

  const [summaryParams, setSummaryParams] = useState({
    policy: null,
    knowledge: null,
    approach: null,
    reasonsForReview: {
      wellbeingEarlyIntervention: null,
      lowRating: null,
      academicEarlyIntervention: null
    },
    feedback: '',
    complete: false
  });

  // Fetch existing BurleyQAReview when the component is first loaded
  useEffect(() => {
    const fetchBurleyQAReview = async () => {
      AjaxUtilities.ajax({
        type: 'GET',
        url: `${burleyQAReviewPath}/${meetingUuid}`,
        success: (res) => {
          setSummaryParams(res);
          setFetchCompleted(true);
        },
        error: () => {
          setFetchCompleted(true);
        }
      });
    };

    fetchBurleyQAReview();
  }, [burleyQAReviewPath, meetingUuid]);

  const handleReturnToQueue: MouseEventHandler<HTMLButtonElement> = (event) => {
    event.preventDefault();
    sendQAActionRequest('return_to_queue', handleSuccess, handleFailed);
  };

  const handleEscalate = () => {
    sendQAActionRequest('escalate', handleSuccess, handleFailed, escalateParams);
  };

  const handleCompleteQA: MouseEventHandler<HTMLButtonElement> = (event) => {
    event.preventDefault();
    sendQAActionRequest('qa_complete', handleSuccess, handleFailed);
  };

  const sendQAActionRequest = (
    action: string,
    successCallback?: Callback,
    failCallback?: Callback,
    params: { action_reason?: string; action_details?: string } = {}
  ) => {
    setSending(true);
    const qaActionParams = {
      actionType: action,
      interactionUuid: meetingUuid,
      interactionType: 'meeting',
      ...params
    };

    AjaxUtilities.ajax({
      type: 'POST',
      url: qaActionPath,
      data: {
        qaAction: qaActionParams
      },
      success: (res) => {
        successCallback?.(res);
      },
      error: (e) => {
        failCallback?.(e);
      }
    });
  };

  const handleCompleteQASummary = (sendComplete: boolean) => {
    setSending(true);

    const burleyQAReviewParams = {
      interactionUuid: meetingUuid,
      ...summaryParams
    };

    if (sendComplete) {
      burleyQAReviewParams.complete = true;
    }

    AjaxUtilities.ajax({
      type: 'POST',
      url: burleyQAReviewPath,
      data: {
        burley_qa_review: snakecaseKeys(burleyQAReviewParams)
      },
      success: () => {
        if (sendComplete) {
          window.location.href = qaQueuePath;
        } else {
          setSending(false);
        }
      },
      error: () => { handleFailed(); }
    });
  };

  // Complete the QA Summary after the user has clicked the button
  const completeQASummary = () => {
    handleCompleteQASummary(true);
  };

  // Every time the summaryParams change then then the data to the server
  useEffect(() => {
    if (fetchCompleted && !sending && !readOnly) {
      handleCompleteQASummary(false);
    }
  }, [summaryParams, readOnly]);

  const handleSuccess = () => {
    setOpenEscalateModal(false);
    setSending(false);
    window.location.href = qaQueuePath;
  };

  const handleFailed = () => {
    setSending(false);
  };

  const QA_REASONS = [
    {
      label: 'Emotionally at risk',
      value: 'emotionally_at_risk'
    },
    {
      label: 'Transcript Issue',
      value: 'transcript_issue'
    }
  ];

  const setEscalateReason = (event: ChangeEvent<HTMLSelectElement>) => {
    event.persist();
    setEscalateParams((es) => ({ ...es, action_reason: event.target.value }));
  };

  const setEscalateDetails = (event: ChangeEvent<HTMLTextAreaElement>) => {
    event.persist();
    setEscalateParams((es) => ({ ...es, action_details: event.target.value }));
  };

  type CustomEvent = {
    target: {
      name: string;
      value: string;
    };
  };

  const setSummaryParamsDetails = (e: ChangeEvent<HTMLInputElement | HTMLTextAreaElement> | CustomEvent) => {
    const { name, value, type } = e.target as HTMLInputElement;
    const { checked } = e.target as HTMLInputElement;

    setSummaryParams((prevSummary) => {
      const newValue = type === 'checkbox' ? checked : value;

      if (['wellbeingEarlyIntervention', 'lowRating', 'academicEarlyIntervention'].includes(name)) {
        return {
          ...prevSummary,
          reasonsForReview: {
            ...prevSummary.reasonsForReview,
            [name]: newValue
          }
        };
      }

      return {
        ...prevSummary,
        [name]: newValue
      };
    });
  };

  const fineToShow = qaResponses?.filter((response) => response === 'fine')?.length || 0;
  const hesitateToShow = qaResponses?.filter((response) => response === 'some_issues')?.length || 0;
  const doNotShow = qaResponses?.filter((response) => response === 'poor')?.length || 0;

  const ratingSegments = (name: string) => [
    { value: 'good', text: 'Good', id: `good_${name}` },
    { value: 'some_issues', text: 'Some issues', id: `some_issues_${name}` },
    { value: 'poor', text: 'Poor', id: `poor_${name}` }
  ];

  const validSegments = (name: string) => [
    { value: 'valid', text: 'Valid', id: `valid_${name}` },
    { value: 'not_valid', text: 'Not valid', id: `not_valid_${name}` }
  ];

  const isCompleteQADisabled =
    readOnly ||
    !summaryParams.policy ||
    !summaryParams.knowledge ||
    !summaryParams.approach ||
    !summaryParams.reasonsForReview.wellbeingEarlyIntervention ||
    !summaryParams.reasonsForReview.lowRating;

  const isReturnToQueueDisabled = !qaReturnable || readOnly || (!burleyQAEnabled && sending);

  const burleyProductName = window.Eureka.translations.burley.productShortName;

  return (
    <div
      id="qa-footer"
      className="u-flex u-flexJustifyEnd u-paddingVSm u-paddingHXl u-gapSm
        d-flex justify-content-end py-2 px-5 gap-2"
    >
      {!isQaLead && (
        <button
          className="btn btn-info"
          type="button"
          disabled={readOnly || (!burleyQAEnabled && sending)}
          onClick={() => {
            setOpenEscalateModal(true);
          }}
        >
          Escalate to QA Lead
        </button>
      )}

      {returnToQueue && (
        <LoadingButton
          label="Return to queue"
          className="btn btn-warning"
          onClick={handleReturnToQueue}
          loading={!burleyQAEnabled && sending}
          disabled={isReturnToQueueDisabled}
        />
      )}

      {completeQA && (
        <button
          className="btn btn-primary"
          type="button"
          disabled={!burleyQAEnabled && sending}
          onClick={(e) => (burleyQAEnabled ? setOpenSummaryModal(true) : handleCompleteQA(e))}
        >
          {readOnly ? 'QA Summary' : 'Complete QA'}
        </button>
      )}

      <Modal
        id="qa-summary-modal"
        className="qa-summary-modal"
        title="Complete QA Summary"
        closeButton={(
          <button
            type="button"
            className="btn-close"
            data-bs-dismiss="modal"
            aria-label="Close"
            onClick={() => setOpenSummaryModal(false)}
          />
        )}
        body={(
          <div className="u-backgroundMainContentBg">
            <div className="card u-fullWidth u-boxShadowNone u-border u-marginTopNone u-paddingNone
              w-100 shadow-none border mt-0 p-0"
            >
              <div className="card-body">
                <div className="u-flex flex-cols u-flexAlignItemsCenter u-flexJustifyCenter
                  d-flex flex-column align-items-center justify-content-center"
                >
                  <h3 id="summary-total-messages">{messageCount}</h3>
                  <h5 className="u-marginNone m-0">
                    Total {pluralize(messageCount, 'Message')} sent by {burleyProductName}
                  </h5>
                </div>
                <div className="u-flex u-flexAlignItemsCenter u-flexJustifyCenter u-paddingLg
                  d-flex align-items-center justify-content-center p-4"
                >
                  <div className="u-flex flex-cols u-flexAlignItemsCenter u-flexJustifyCenter
                    d-flex flex-column align-items-center justify-content-center"
                  >
                    <span className="btn btn-summary u-marginBottomSm btn-success mb-0">
                      <SvgIcon icon="check_circle" className="icon-sm u-marginRightSm me-2" />
                      Fine
                    </span>
                    <span id="summary-fine-messages">
                      {fineToShow} {pluralize(fineToShow, 'response')}
                    </span>
                  </div>
                  <div className="u-marginLeftLg ms-4" />
                  <div className="u-flex flex-cols u-flexAlignItemsCenter u-flexJustifyCenter
                    d-flex flex-column align-items-center justify-content-center"
                  >
                    <span className="btn btn-summary u-marginBottomSm btn-default mb-0">
                      <SvgIcon icon="question_exchange" className="icon-sm u-marginRightSm me-2" />
                      Some issues
                    </span>
                    <span id="summary-hesitate-to-show-messages">
                      {hesitateToShow} {pluralize(hesitateToShow, 'response')}
                    </span>
                  </div>
                  <div className="u-marginLeftLg ms-4" />
                  <div className="u-flex flex-cols u-flexAlignItemsCenter u-flexJustifyCenter
                    d-flex flex-column align-items-center justify-content-center"
                  >
                    <span className="btn btn-summary u-marginBottomSm btn-danger mb-2">
                      <SvgIcon icon="block" className="icon-sm u-marginRightSm me-2" />
                      Poor
                    </span>
                    <span id="summary-do-not-show-messages">
                      {doNotShow} {pluralize(doNotShow, 'response')}
                    </span>
                  </div>
                </div>
              </div>
            </div>

            <form>
              <FormGroup controlId="policy">
                <ControlLabel bsClass="u-fontWeightBold fw-bold">Policy *</ControlLabel>
                <div className="row">
                  <div className="col-xs-6">
                    <p>
                      Did {burleyProductName} stick to the Tiered Academic Support framework and provide ethical help?
                    </p>
                  </div>
                  <div className="col-xs-6 text-right">
                    <SegmentedRadioButtons
                      segments={ratingSegments('policy')}
                      name="policy"
                      onChange={setSummaryParamsDetails}
                      value={summaryParams.policy}
                      disabled={readOnly}
                      controlled
                      managedValue
                      vertical={false}
                      labelClassNames="u-flexGrow1 flex-grow-1"
                    />
                  </div>
                </div>
              </FormGroup>
              <FormGroup controlId="knowledge">
                <ControlLabel bsClass="u-fontWeightBold fw-bold">Knowledge *</ControlLabel>
                <div className="row">
                  <div className="col-xs-6">
                    <p>Was the information provided by {burleyProductName} accurate?</p>
                  </div>
                  <div className="col-xs-6 text-right">
                    <SegmentedRadioButtons
                      segments={ratingSegments('knowledge')}
                      name="knowledge"
                      onChange={setSummaryParamsDetails}
                      value={summaryParams.knowledge}
                      disabled={readOnly}
                      controlled
                      managedValue
                      vertical={false}
                      labelClassNames="u-flexGrow1 flex-grow-1"
                    />
                  </div>
                </div>
              </FormGroup>
              <FormGroup controlId="approach">
                <ControlLabel bsClass="u-fontWeightBold fw-bold">Approach *</ControlLabel>
                <div className="row">
                  <div className="col-xs-6">
                    <p>
                      Was the language used by {burleyProductName} appropriate and
                      did it respond to the student query effectively?
                    </p>
                  </div>
                  <div className="col-xs-6 text-right">
                    <SegmentedRadioButtons
                      segments={ratingSegments('approach')}
                      name="approach"
                      onChange={setSummaryParamsDetails}
                      value={summaryParams.approach}
                      disabled={readOnly}
                      controlled
                      managedValue
                      vertical={false}
                      labelClassNames="u-flexGrow1 flex-grow-1"
                    />
                  </div>
                </div>
              </FormGroup>
              <FormGroup controlId="reasons">
                <ControlLabel bsClass="u-fontWeightBold fw-bold">Reasons for review *</ControlLabel>
                <div className="row">
                  <div className="col-xs-6">
                    <p className="u-marginBottomNone u-marginTopSm mb-0 mt-2">Wellbeing Early Intervention</p>
                  </div>
                  <div className="col-xs-6 text-right">
                    <SegmentedRadioButtons
                      segments={validSegments('wellbeingEarlyIntervention')}
                      name="wellbeingEarlyIntervention"
                      onChange={setSummaryParamsDetails}
                      value={summaryParams.reasonsForReview.wellbeingEarlyIntervention}
                      disabled={readOnly}
                      controlled
                      managedValue
                      vertical={false}
                      labelClassNames="u-flexGrow1 flew-grow-1"
                    />
                  </div>
                </div>
                <div className="row">
                  <div className="col-xs-6">
                    <p className="u-marginBottomNone u-marginTopSm mb-0 mt-2">Low rating</p>
                  </div>
                  <div className="col-xs-6 text-right">
                    <SegmentedRadioButtons
                      segments={validSegments('low_rating')}
                      name="lowRating"
                      onChange={setSummaryParamsDetails}
                      value={summaryParams.reasonsForReview.lowRating}
                      disabled={readOnly}
                      controlled
                      managedValue
                      vertical={false}
                      labelClassNames="u-flexGrow1 flew-grow-1"
                    />
                  </div>
                </div>
              </FormGroup>
              <FormGroup>
                <ControlLabel bsClass="u-fontWeightBold fw-bold">Additional flags</ControlLabel>
                <div className="academic-flag-filter">
                  <label>
                    <input
                      type="checkbox"
                      name="academicEarlyIntervention"
                      checked={summaryParams.reasonsForReview.academicEarlyIntervention === 'valid'}
                      className="u-marginRightSm me-2"
                      onChange={(e) => {
                        const value = e.target.checked ? 'valid' : 'not_valid';
                        setSummaryParamsDetails({
                          target: {
                            name: 'academicEarlyIntervention',
                            value
                          }
                        });
                      }}
                      disabled={readOnly}
                    />
                    Academic Early Intervention Flag
                  </label>
                </div>
              </FormGroup>
              <FormGroup controlId="feedback">
                <ControlLabel>General Comments</ControlLabel>
                <FormControl
                  componentClass="textarea"
                  name="feedback"
                  value={summaryParams.feedback || ''}
                  onChange={(e) => setSummaryParamsDetails(e as unknown as ChangeEvent<HTMLTextAreaElement>)}
                  disabled={readOnly}
                />
              </FormGroup>
            </form>
          </div>
        )}
        footer={(
          <div className="u-flex u-flexJustifyCenter d-flex justify-content-center">
            <Button
              bsStyle="primary"
              type="button"
              onClick={completeQASummary}
              disabled={isCompleteQADisabled}
            >
              Complete QA
            </Button>
          </div>
        )}
        show={openSummaryModal}
        onClose={() => setOpenSummaryModal(false)}
      />

      <Modal
        id="qa-escalate-modal"
        className="qa-escalate-modal"
        title="Escalate to QA Lead"
        closeButton={(
          <button
            type="button"
            className="btn-close"
            data-bs-dismiss="modal"
            aria-label="Close"
            onClick={() => setOpenEscalateModal(false)}
          />
        )}
        body={(
          <div className="u-backgroundMainContentBg">
            <div className="u-fullWidth w-100 action-reason">
              <div className="qa-alert-message u-marginVMd my-3">
                <SvgIcon icon="warning" className="icon-md" />
                <span>Are there any additional flags that should have been raised?</span>
              </div>
              <select id="action-reason" className="form-control" onChange={setEscalateReason}>
                {QA_REASONS.map((flagOption) => (
                  <option
                    key={flagOption.value}
                    value={flagOption.value}
                  >
                    {flagOption.label}
                  </option>
                ))}
              </select>
            </div>
            <div className="u-fullWidth u-marginVMd w-100 my-3">
              <div className="qa-alert-message u-marginVMd my-3">
                <SvgIcon icon="warning" className="icon-md" />
                <span>Please provide general comments on why the student was flagged for the above reasons.</span>
              </div>
              <textarea
                value={escalateParams.action_details}
                onChange={setEscalateDetails}
                rows={2}
                placeholder="Write your feedback here..."
                className="form-control"
              />
            </div>
          </div>
        )}
        footer={(
          <div className="u-flex u-flexJustifyCenter d-flex justify-content-between">
            <Button bsStyle="primary" onClick={handleEscalate}>
              Submit
            </Button>
          </div>
        )}
        show={openEscalateModal}
        onClose={() => {
          setOpenEscalateModal(false);
        }}
      />
    </div>
  );
};

export default Footer;
